import { useTranslate } from "@tolgee/react";
import { Check } from "lucide-react";
import { Button } from "#/components/ui/button";
import { cn } from "#/lib/utils";
import {
	TradePopoverSelect,
	type TradePopoverSelectOption,
} from "../trade-popover-select.tsx";
import type { PositionsTab, SideFilter } from "./types/positions.types.ts";

type PositionsTabsProps = {
	tab: PositionsTab;
	positionCount: number;
	openOrderCount: number;
	balanceCount: number;
	sideFilter: SideFilter;
	hideSmallBalances: boolean;
	onTabChange: (nextTab: PositionsTab) => void;
	onSideFilterChange: (filter: SideFilter) => void;
	onHideSmallBalancesChange: (hide: boolean) => void;
};

const tabKeys: Array<{
	key: PositionsTab;
	labelKey: string;
	showCount?: boolean;
}> = [
	{ key: "balances", labelKey: "positions.tab.balances", showCount: true },
	{ key: "positions", labelKey: "positions.tab.positions", showCount: true },
	{ key: "orders", labelKey: "positions.tab.open-orders", showCount: true },
	{ key: "twap", labelKey: "positions.tab.twap" },
	{ key: "trade-history", labelKey: "positions.tab.trade-history" },
	{ key: "funding-history", labelKey: "positions.tab.funding-history" },
	{ key: "order-history", labelKey: "positions.tab.order-history" },
];

const filterOptions: Array<{ value: SideFilter; labelKey: string }> = [
	{ value: "all", labelKey: "filter.all" },
	{ value: "long", labelKey: "filter.long" },
	{ value: "short", labelKey: "filter.short" },
];

export function PositionsTabs({
	tab,
	positionCount,
	openOrderCount,
	balanceCount,
	sideFilter,
	hideSmallBalances,
	onTabChange,
	onSideFilterChange,
	onHideSmallBalancesChange,
}: PositionsTabsProps) {
	const { t } = useTranslate();
	const sideFilterOptions: Array<TradePopoverSelectOption<SideFilter>> =
		filterOptions.map((option) => ({
			value: option.value,
			label: t(option.labelKey),
		}));

	function getCount(key: PositionsTab) {
		if (key === "balances") return balanceCount;
		if (key === "positions") return positionCount;
		if (key === "orders") return openOrderCount;
		return null;
	}

	return (
		<div className="trade-panel-handle flex items-center justify-between border-b border-trade-border-subtle px-4">
			<div className="flex items-center gap-0 overflow-x-auto scrollbar-none">
				{tabKeys.map((item) => {
					const count = item.showCount ? getCount(item.key) : null;
					const isActive = tab === item.key;
					return (
						<button
							key={item.key}
							type="button"
							onClick={() => onTabChange(item.key)}
							className={`relative cursor-pointer whitespace-nowrap px-3 py-3 transition-colors ${
								isActive
									? "text-label2-medium text-trade-text-strong"
									: "text-label2 text-trade-text-secondary hover:text-trade-text"
							}`}
						>
							{t(item.labelKey)}
							{count !== null && count > 0 ? ` (${count})` : ""}
							{isActive && (
								<span className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary" />
							)}
						</button>
					);
				})}
			</div>
			{tab === "balances" && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={() => onHideSmallBalancesChange(!hideSmallBalances)}
					onMouseDown={(e) => e.stopPropagation()}
					onPointerDown={(e) => e.stopPropagation()}
					className={cn(
						"h-auto shrink-0 gap-1.5 px-2 py-0 text-trade-text-secondary hover:bg-transparent",
						hideSmallBalances && "text-trade-text",
					)}
				>
					<span
						className={cn(
							"inline-flex h-4.5 w-4.5 items-center justify-center rounded-[5px] border border-trade-border bg-trade-surface transition-[border-color,background-color,box-shadow]",
							hideSmallBalances &&
								"border-trade-accent bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-theme-primary-normal)_22%,transparent)_0%,color-mix(in_srgb,var(--color-theme-primary-normal)_14%,transparent)_100%)]",
						)}
						aria-hidden="true"
					>
						{hideSmallBalances && (
							<Check className="h-3 w-3 text-trade-accent" />
						)}
					</span>
					{t("positions.hide-small-balances")}
				</Button>
			)}
			{tab !== "balances" && (
				<TradePopoverSelect
					ariaLabel={t("positions.filter")}
					align="end"
					onValueChange={onSideFilterChange}
					options={sideFilterOptions}
					showValue={false}
					sideOffset={4}
					triggerClassName="hidden md:flex w-auto shrink-0 min-w-0 gap-1 border-0 px-2 py-1.5 text-caption1 text-trade-text-secondary shadow-none hover:border-transparent hover:bg-transparent hover:text-trade-text focus-visible:border-transparent focus-visible:ring-0 [&_[data-slot=popover-trigger-label]]:text-caption1 [&_[data-slot=popover-trigger-label]]:text-trade-text-secondary [&_[data-slot=popover-trigger-icon]]:size-3 [&_[data-slot=popover-trigger-icon]]:text-trade-text-secondary"
					triggerLabel={t("positions.filter")}
					value={sideFilter}
				/>
			)}
		</div>
	);
}
